import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import apiService from "../../services/apiService";
import productService from "../../services/productService";
import { useProductsCrud } from "../../hooks/useProductsCrud";

const localCacheKey = "productReadCache";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function ReadProducts() {
  const navigate = useNavigate();

  const { deleteProduct, updateProduct } = useProductsCrud();
const [searchText, setSearchText] = useState("");
  // ✅ Safe read from localStorage
  const [cachedData, setCachedData] = useState(() => {
    try {
      const cache = localStorage.getItem(localCacheKey);
      return cache ? JSON.parse(cache) : null;
    } catch (error) {
      console.warn("Invalid JSON in localStorage. Clearing corrupted cache.");
      localStorage.removeItem(localCacheKey);
      return null;
    }
  });

  const [localData, setLocalData] = useState(cachedData?.products || []);
  const [loading, setLoading] = useState(!cachedData);
  const [selectedRows, setSelectedRows] = useState([]);

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  const enrichProducts = useCallback((products, categories, subCategories) => {
    return products.map((product, index) => {
      const category = categories.find((cat) => cat.id === product.category_id);
      const subCategory = subCategories.find((sub) => sub.id === product.sub_category_id);
      return {
        ...product,
        category_name: category ? category.name : "Unknown",
        sub_category_name: subCategory ? subCategory.name : "Unknown",
        sl: index + 1,
      };
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [productRes, categoryRes, subCategoryRes] = await Promise.all([
        productService.getAllProducts(),
        apiService.getAll("backend/categories"),
        apiService.getAll("backend/sub-categories"),
      ]);

   console.log("PRODUCT API BASE URL:", productRes?.config?.baseURL);
console.log("PRODUCT API URL:", productRes?.config?.url);
console.log("PRODUCT API RESPONSE:", productRes?.data);
console.log(
  "FIRST PRODUCT:",
  Array.isArray(productRes?.data?.data)
    ? productRes.data.data[0]
    : productRes?.data?.data?.data?.[0]
);
const firstProduct = productRes?.data?.data?.[0];

console.log("PRODUCT PRICE:", firstProduct?.price);
console.log("PRODUCT SKU:", firstProduct?.sku);
console.log("PRODUCT QTY:", firstProduct?.qty);
console.log("PRODUCT VARIANTS:", firstProduct?.variant_items);
console.log("PRODUCT IMAGE URL:", firstProduct?.thumb_image_url);
const products =
  productRes?.data?.data?.data ??
  productRes?.data?.data ??
  productRes?.data ??
  [];

const categories =
  categoryRes?.data?.data ??
  categoryRes?.data ??
  [];

const subCategories =
  subCategoryRes?.data?.data ??
  subCategoryRes?.data ??
  [];



    

const enriched = products.map((product, index) => {
  const category = categories.find(
    (cat) => String(cat.id) === String(product.category_id)
  );

  const subCategory = subCategories.find(
    (sub) => String(sub.id) === String(product.sub_category_id)
  );

  const variant = product.variant_items?.[0] || {};

  return {
    ...product,

    thumb_image:
      product.thumb_image_url ||
      product.thumb_image ||
      "",

    price:
      variant.variant_offer_price ??
      variant.variant_price ??
      product.price ??
      "",

    sku:
      variant.variant_sku ??
      product.sku ??
      "",

    qty:
      variant.variant_qty ??
      product.qty ??
      "",

    category_name: category?.name || "Unknown",
    sub_category_name: subCategory?.name || "Unknown",
    sl: index + 1,
  };
});





      const newCache = {
        products: enriched,
        categories,
        subCategories,
      };

      let existingCache = null;
      try {
        existingCache = JSON.parse(localStorage.getItem(localCacheKey));
      } catch (e) {
        console.warn("Existing cache is invalid. Replacing.");
      }

      if (
        !existingCache ||
        JSON.stringify(existingCache.products) !== JSON.stringify(enriched) ||
        JSON.stringify(existingCache.categories) !== JSON.stringify(categories) ||
        JSON.stringify(existingCache.subCategories) !== JSON.stringify(subCategories)
      ) {
        try {
          localStorage.setItem(localCacheKey, JSON.stringify(newCache));
        } catch (e) {
          console.error("Failed to write to localStorage", e);
        }
        setCachedData(newCache);
      }

      setLocalData(enriched);
    } catch (error) {
      console.error("Failed to load data:", error);
      showSwal("Error", "Failed to load product data.", "error");
    } finally {
      setLoading(false);
    }
  }, [enrichProducts, showSwal]);

  useEffect(() => {
    if (cachedData) {
      setLocalData(cachedData.products);
      setLoading(false);
      loadData(); // Background refresh
    } else {
      loadData();
    }
  }, [cachedData, loadData]);


const updateLocalDataCache = (updatedProducts) => {
  if (!cachedData) return;

  const enriched = updatedProducts.map((product, index) => {
    const variant = product.variant_items?.[0] || {};

    return {
      ...product,

      thumb_image:
        product.thumb_image_url ||
        product.thumb_image ||
        "",

      price:
        variant.variant_offer_price ??
        variant.variant_price ??
        product.price ??
        "",

      sku: variant.variant_sku ?? product.sku ?? "",

      qty: variant.variant_qty ?? product.qty ?? "",

      sl: index + 1,
    };
  });

  const newCache = {
    products: enriched,
    categories: cachedData.categories,
    subCategories: cachedData.subCategories,
  };

  setLocalData(enriched);
  setCachedData(newCache);

  try {
    localStorage.setItem(localCacheKey, JSON.stringify(newCache));
  } catch (error) {
    console.error("Failed to update localStorage", error);
  }
};



  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
    });

    if (confirm.isConfirmed) {
      try {
        await deleteProduct(id);
        const updated = localData.filter((item) => item.id !== id);
        updateLocalDataCache(updated);
        showSwal("Deleted!", "Product has been deleted.", "success");
      } catch (error) {
        console.error("Delete error:", error.response?.data || error);
        showSwal("Error", error.response?.data?.message || "Failed to delete.", "error");
      }
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      const newStatus = item.status ? 0 : 1;

      await productService.changeProductStatus(item.id, newStatus);

      const updatedProducts = localData.map((product) =>
        product.id === item.id
          ? { ...product, status: newStatus }
          : product
      );

      updateLocalDataCache(updatedProducts);

      showSwal("Success!", "Product status updated.", "success");
    } catch (error) {
      console.error("Status update failed:", error.response?.data || error);
      showSwal(
        "Error!",
        error.response?.data?.message || "Failed to update product status.",
        "error"
      );
    }
  }; 

  const handleSelectRow = (id) => {
  setSelectedRows((prev) =>
    prev.includes(id)
      ? prev.filter((item) => item !== id)
      : [...prev, id]
  );
};

const handleSelectAll = () => {
  if (selectedRows.length === localData.length) {
    setSelectedRows([]);
  } else {
    setSelectedRows(localData.map((item) => item.id));
  }
};

const handleBulkDelete = async () => {
  if (selectedRows.length === 0) {
    showSwal("Warning", "Please select products.", "warning");
    return;
  }

  const confirm = await Swal.fire({
    title: "Delete Selected Products?",
    text: `You are deleting ${selectedRows.length} products.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes Delete",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#3b82f6",
  });

  if (!confirm.isConfirmed) return;

  try {
    await productService.bulkDeleteProducts(selectedRows);

    const updated = localData.filter(
      (item) => !selectedRows.includes(item.id)
    );

    updateLocalDataCache(updated);

    setSelectedRows([]);

    showSwal(
      "Success",
      "Products deleted successfully.",
      "success"
    );
  } catch (error) {
    console.error(error);
    showSwal(
      "Error",
      error.response?.data?.message || "Bulk delete failed",
      "error"
    );
  }
};

const handleBulkApproval = async (status) => {
  if (selectedRows.length === 0) {
    showSwal("Warning", "Please select products.", "warning");
    return;
  }

  const confirm = await Swal.fire({
    title: status
      ? "Approve Selected Products?"
      : "Unapprove Selected Products?",
    text: `You selected ${selectedRows.length} products.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: status ? "Approve" : "Unapprove",
    cancelButtonText: "Cancel",
    confirmButtonColor: status ? "#198754" : "#dc3545",
  });

  if (!confirm.isConfirmed) return;

  try {
    await productService.bulkApproveProducts(
      selectedRows,
      status
    );

    const updatedProducts = localData.map((product) =>
      selectedRows.includes(product.id)
        ? {
            ...product,
            is_approved: status,
          }
        : product
    );

    updateLocalDataCache(updatedProducts);

    setSelectedRows([]);

    showSwal(
      "Success",
      status
        ? "Products approved successfully."
        : "Products unapproved successfully.",
      "success"
    );
  } catch (error) {
    console.error(error);

    showSwal(
      "Error",
      error.response?.data?.message ||
        "Bulk approval failed",
      "error"
    );
  }
};

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4 ">
     <div className="d-flex justify-content-between align-items-center mb-4">
  <button
    className="btn btn-secondary me-3 "
    onClick={() => navigate(-1) || navigate("/admin/dashboard") }
  >
    Back
  </button>

  <div className="d-flex gap-2">
    <button 
      type="button"
      className="btn  "
        style={{
    backgroundColor: "#3b82f6",
    borderColor: "#3b82f6",
    color: "#fff",
  }}
      onClick={() => navigate("/admin/product/create")}
    >
      Create Product
    </button>

    <button
      type="button"
      className="btn btn-success"
      onClick={() => navigate("/admin/product/create-bulk")}
    >
      Create Bulk Product
    </button>
  </div>
</div>

        <div className="card shadow">
        
<div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
  <h4 className="mb-0">Seller All Products</h4>

  <input
    type="text"
    className="form-control"
    placeholder="Search products..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
      width: "260px",
      maxWidth: "100%",
      borderRadius: "6px",
    }}
  />
</div>



          <div className="card-body"> 

<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
  {/* Left side: Bulk actions */}
  <div className="d-flex flex-wrap align-items-center gap-2">
    <button
      className="btn"
      onClick={handleSelectAll}
      style={{
        backgroundColor: "#3b82f6",
        borderColor: "#3b82f6",
        color: "#fff",
      }}
    >
      {selectedRows.length === localData.length
        ? "Unselect All"
        : "Select All"}
    </button>

    <button
      className="btn btn-success"
      onClick={() => handleBulkApproval(true)}
      disabled={selectedRows.length === 0}
    >
      Approve Selected ({selectedRows.length})
    </button>

    <button
      className="btn btn-warning text-dark"
      onClick={() => handleBulkApproval(false)}
      disabled={selectedRows.length === 0}
    >
      Unapprove Selected
    </button>

    <button
      className="btn btn-danger"
      onClick={handleBulkDelete}
      disabled={selectedRows.length === 0}
    >
      Delete Selected
    </button>
  </div>

  {/* Right side: Export buttons */}
  <div className="d-flex flex-wrap align-items-center gap-2">
    <button
      type="button"
      className="btn btn-success"
      onClick={() => window.dispatchEvent(new Event("download-products-excel"))}
    >
      <i className="bx bx-spreadsheet me-1"></i>
      Download Excel
    </button>

    <button
      type="button"
      className="btn btn-danger"
      onClick={() => window.dispatchEvent(new Event("download-products-pdf"))}
    >
      <i className="bx bxs-file-pdf me-1"></i>
      Download PDF
    </button>
  </div>
</div>

<div className="mb-3 mt-5">
  <small className="text-muted fw-semibold">
    {selectedRows.length > 0
      ? `${selectedRows.length} item${
          selectedRows.length > 1 ? "s" : ""
        } selected`
      : "No items selected"}
  </small>
</div>
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "200px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <CrudTable
                data={localData}
                selectedRows={selectedRows}
                onSelectRow={handleSelectRow}
                onEditLink={(row) => `/admin/products/edit/${row.id}`}


                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                  searchText={searchText}
                columns={[
                  "sl",
                  "thumb_image",
                  "name",
                  "price",
                  "sku",
                  "qty",
                  "status", 
                
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
